import { CreateFullPageStep } from '@carbon/ibm-products';
import { Button } from '@carbon/react';
import { Dispatch, SetStateAction } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import cx from 'classnames';

import sharedMessages from 'Components/utils/sharedMessages';

import { LocalCategory, LocalPerson, PersonFormState } from '../../types';
import styles from '../../InitialSetupModal.module.scss';
import CategoriesStep from '../CategoriesStep/CategoriesStep';
import PersonFormFields from './components/PersonFormFields';
import PeopleList from './components/PeopleList';

type Props = {
    title: MessageDescriptor;
    categoriesTitle: MessageDescriptor;
    categoriesDescription: MessageDescriptor;
    labels: {
        addPeopleTitle: MessageDescriptor;
        currentPeopleTitle: MessageDescriptor;
        personCategory: MessageDescriptor;
        selectCategory: MessageDescriptor;
        firstName: MessageDescriptor;
        lastName: MessageDescriptor;
        role: MessageDescriptor;
        availability: MessageDescriptor;
        description: MessageDescriptor;
        visibleForGuests: MessageDescriptor;
    };
    categories: LocalCategory[];
    people: LocalPerson[];
    personForm: PersonFormState;
    isPersonFormEmailValid: boolean;
    isPersonFormPhoneValid: boolean;
    isPersonDraftValid: boolean;
    isDisabled: boolean;
    onMount?: () => void;
    setPersonForm: Dispatch<SetStateAction<PersonFormState>>;
    onAddCategory: (name: string) => void;
    onRemoveCategory: (categoryId: string) => void;
    onAddPerson: () => void;
    onRemovePerson: (personId: string) => void;
    getCategoryName: (categoryId: string) => string;
};

const PeopleStep = ({
    title,
    categoriesTitle,
    categoriesDescription,
    labels,
    categories,
    people,
    personForm,
    isPersonFormEmailValid,
    isPersonFormPhoneValid,
    isPersonDraftValid,
    isDisabled,
    onMount,
    setPersonForm,
    onAddCategory,
    onRemoveCategory,
    onAddPerson,
    onRemovePerson,
    getCategoryName,
}: Props) => {
    const intl = useIntl();

    return (
        <CreateFullPageStep
            title={intl.formatMessage(title)}
            fieldsetLegendText=""
            disableSubmit={isDisabled}
            onMount={onMount}
        >
            <CategoriesStep
                title={categoriesTitle}
                description={categoriesDescription}
                categories={categories}
                isDisabled={isDisabled}
                onAddCategory={onAddCategory}
                onRemoveCategory={onRemoveCategory}
                renderStep={false}
            />

            <div className={cx(styles.stepContainer, styles.peopleLayoutContainer)}>
                <div className={styles.peopleLayout}>
                    <div className={styles.peopleFormColumn}>
                        <h4 className={styles.sectionTitle}>{intl.formatMessage(labels.addPeopleTitle)}</h4>

                        <PersonFormFields
                            personForm={personForm}
                            categories={categories}
                            isEmailValid={isPersonFormEmailValid}
                            isPhoneValid={isPersonFormPhoneValid}
                            labels={labels}
                            setPersonForm={setPersonForm}
                        />

                        <div>
                            <Button
                                kind="primary"
                                disabled={!isPersonDraftValid}
                                onClick={onAddPerson}
                                className={styles.addPersonButton}
                            >
                                {intl.formatMessage(sharedMessages.add)}
                            </Button>
                        </div>
                    </div>

                    <div className={styles.peopleListColumn}>
                        <h4 className={styles.sectionTitle}>{intl.formatMessage(labels.currentPeopleTitle)}</h4>

                        <PeopleList
                            people={people}
                            personCategoryLabel={labels.personCategory}
                            getCategoryName={getCategoryName}
                            onRemovePerson={onRemovePerson}
                        />
                    </div>
                </div>
            </div>
        </CreateFullPageStep>
    );
};

export default PeopleStep;
